import axios from 'axios';

const SESSION_TIMEOUT_MS = 5000;

/**
 * Dedicated API client for the Compliance Session Manager.
 *
 * Security:
 *  - Enforces HTTPS-only (rejects any non-https base URL).
 *  - 5 second timeout on every request.
 *  - Authenticated by X-API-KEY (same scheme as other /mobi/ endpoints).
 *
 * Components MUST use this client rather than calling fetch/axios directly.
 */
const assertHttps = (baseUrl) => {
  if (!baseUrl || !/^https:\/\//i.test(baseUrl)) {
    const err = new Error('Session API base URL must use HTTPS');
    err.type = 'config';
    throw err;
  }
};

const buildHeaders = (token) => (token ? { 'X-API-KEY': token } : {});

/**
 * Resolve a compliance session by its opaque session ID.
 * Returns the raw response data (validation happens at the caller via Zod).
 * Throws an Error with a `.type` of: invalid | expired | consumed | network | timeout.
 */
export const resolveSession = async ({ baseUrl, token, sessionId }) => {
  assertHttps(baseUrl);

  try {
    const response = await axios.get(
      `${baseUrl}/mobi/sessions/${encodeURIComponent(sessionId)}`,
      {
        headers: buildHeaders(token),
        timeout: SESSION_TIMEOUT_MS
      }
    );
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * Mark a session consumed (idempotent on the backend).
 * Best-effort: callers may ignore failures since the backend also enforces.
 */
export const consumeSession = async ({ baseUrl, token, sessionId }) => {
  assertHttps(baseUrl);

  try {
    const response = await axios.patch(
      `${baseUrl}/mobi/sessions/${encodeURIComponent(sessionId)}/consume`,
      {},
      {
        headers: buildHeaders(token),
        timeout: SESSION_TIMEOUT_MS
      }
    );
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * Map an axios error into a typed session error.
 */
const normalizeError = (err) => {
  const out = new Error(err.message || 'Session request failed');

  if (err.code === 'ECONNABORTED') {
    out.type = 'timeout';
    return out;
  }

  const status = err.response?.status;
  const bodyType = err.response?.data?.errorType;

  if (bodyType) {
    out.type = bodyType; // invalid | expired | consumed
    return out;
  }

  if (status === 404) {
    out.type = 'invalid';
  } else if (status === 410) {
    out.type = 'expired';
  } else if (status === 409) {
    out.type = 'consumed';
  } else {
    out.type = 'network';
  }

  return out;
};

const sessionClient = { resolveSession, consumeSession };
export default sessionClient;
